import { type ExternalResource, CallState } from "@b2bportal/core-types";
import type { ThunkErrorType } from "@b2bportal/core-utilities";
import type {
  AirportRegion,
  BenchmarkProduct,
  DateRange,
  PackageRoom,
  PackageSummary,
} from "@b2bportal/packages-api";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { fetchPackageHotelAvailability } from "./thunks/fetchPackageHotelAvailability";
import { fetchHotelsShop } from "./thunks/fetchPackageHotelsShop";

export type TPackagesByLodgingId = {
  packagesByLodgingId: { [key: string]: PackageSummary };
  searchId: string;
};

export type TPackagesByRateId = {
  packagesByRateId: { [key: string]: PackageRoom };
};

export type PackageAvailabilityMeta = {
  stayDates?: DateRange;
  destinationAirport?: AirportRegion;
  benchmarkProducts?: BenchmarkProduct[];
};

export type PackageHotelAvailabilityState = {
  meta: PackageAvailabilityMeta;

  availability: ExternalResource<
    TPackagesByLodgingId,
    ThunkErrorType<{ error: string }>
  >;

  shop: ExternalResource<TPackagesByRateId, ThunkErrorType<{ error: string }>>;
};

export type PackageHotelState = PackageHotelAvailabilityState;

export const initialHotelState: PackageHotelState = {
  availability: { state: CallState.NotCalled },
  shop: { state: CallState.NotCalled },
  meta: {},
};

export const packageHotelSlice = createSlice({
  name: "package/hotel",
  initialState: initialHotelState,
  reducers: {
    setHotelAvailabilityPackagePricing: (
      state,
      action: PayloadAction<TPackagesByLodgingId>
    ) => {
      if (state.availability.data?.searchId === action.payload.searchId) {
        state.availability.data.packagesByLodgingId = {
          ...state.availability.data?.packagesByLodgingId,
          ...action.payload.packagesByLodgingId,
        };
      }
    },
    setMeta: (state, action: PayloadAction<PackageAvailabilityMeta>) => {
      state.meta = {
        ...state.meta,
        ...action.payload,
      };
    },
    clearMeta: (state) => {
      state.meta = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPackageHotelAvailability.pending, (state) => {
        state.availability.state = CallState.InProcess;
      })
      .addCase(fetchPackageHotelAvailability.fulfilled, (state, action) => {
        state.availability.state = CallState.Success;
        state.availability.data = action.payload;
      })
      .addCase(fetchPackageHotelAvailability.rejected, (state) => {
        state.availability.state = CallState.Failed;
      })
      .addCase(fetchHotelsShop.pending, (state) => {
        state.shop.state = CallState.InProcess;
      })
      .addCase(fetchHotelsShop.fulfilled, (state, action) => {
        state.shop.state = CallState.Success;
        state.shop.data = action.payload;
      })
      .addCase(fetchHotelsShop.rejected, (state) => {
        state.shop.state = CallState.Failed;
      });
  },
});

export const PackageHotelActions = packageHotelSlice.actions;
export default packageHotelSlice.reducer;
